import { useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { ConfirmDialog } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 33 point 8 — "Send/Deduct Diamonds", exact mirror of
 * CoinAdjustmentsPage.tsx over routes/admin/diamonds.js instead of
 * coin-adjustments.js. Same division of responsibility: this page has no
 * privileged logic of its own — the non-negative guarantee and the
 * ledger/audit-log write all happen server-side (admin_adjust_diamond_balance,
 * migration 0069). Deliberately NOT gated behind StepUpDialog — routes/
 * admin/diamonds.js never calls requireStepUp (same as coin-adjustments.js),
 * so a step-up token here would just be dead weight the server ignores; the
 * real bar is the super_admin-only route in App.tsx plus this page's own
 * ConfirmDialog, matching CoinAdjustmentsPage.tsx exactly.
 *
 * Flow: search by the user's PUBLIC User ID -> see their username, ID, and
 * a freshly-fetched coin + diamond balance immediately -> choose Send or
 * Deduct, an amount, and a required reason -> confirm. The balance shown on
 * the confirm dialog is re-fetched right before it opens, never reused from
 * the initial search.
 */
interface FoundUser {
  id: string; // internal UUID — only used to target the adjustment call
  user_id: number; // public ID
  username: string;
  avatar_url: string | null;
  coin_balance: number;
  diamond_balance: number;
}

export function DiamondAdjustmentsPage() {
  const [searchId, setSearchId] = useState("");
  const [found, setFound] = useState<FoundUser | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [direction, setDirection] = useState<"send" | "deduct">("send");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRefreshingConfirm, setIsRefreshingConfirm] = useState(false);
  const [confirmBalance, setConfirmBalance] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function lookup(query: string): Promise<FoundUser> {
    return apiRequest<FoundUser>("/admin/diamonds/lookup", { params: { query } });
  }

  async function handleSearch() {
    if (!searchId.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      const user = await lookup(searchId.trim());
      setFound(user);
    } catch (err) {
      setSearchError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsSearching(false);
    }
  }

  function resetForm() {
    setAmount("");
    setReason("");
    setDirection("send");
  }

  async function openConfirm() {
    if (!found || !amount || Number(amount) <= 0 || !reason.trim()) return;
    // Fresh balance, fetched right now — never the number already sitting
    // in `found` from whenever the search happened.
    setIsRefreshingConfirm(true);
    setIsConfirmOpen(true);
    try {
      const fresh = await lookup(String(found.user_id));
      setConfirmBalance(fresh.diamond_balance);
      setFound(fresh); // keep the card in sync with the live number too
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not refresh balance", "error");
      setIsConfirmOpen(false);
    } finally {
      setIsRefreshingConfirm(false);
    }
  }

  async function handleConfirm() {
    if (!found) return;
    setIsSubmitting(true);
    try {
      const result = await apiRequest<{ ok: boolean; balance: number }>("/admin/diamonds", {
        method: "POST",
        body: { id: found.id, amount: Number(amount), direction, note: reason.trim() },
      });
      showToast(
        `${direction === "send" ? "Sent" : "Deducted"} ${Number(amount).toLocaleString()} diamonds — new balance ${result.balance.toLocaleString()}`,
        "success"
      );
      setFound((f) => (f ? { ...f, diamond_balance: result.balance } : f));
      setIsConfirmOpen(false);
      resetForm();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not complete adjustment", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AppShell title="Send / Deduct Diamonds">
      <p className="mb-4 max-w-xl text-xs text-ink-faint">
        Every adjustment here goes through the same server-side diamond ledger as every other diamond-affecting
        action in the app — never a direct balance edit. A deduction can never take a user's balance below zero, and
        every adjustment is recorded with the amount, direction, your reason, your admin account, and a timestamp.
      </p>

      <Card className="max-w-xl p-5">
        <div className="flex items-end gap-2">
          <Input
            label="Find user by public User ID"
            placeholder="e.g. 74848506"
            value={searchId}
            onChange={(e) => {
              setSearchId(e.target.value);
              setFound(null);
              setSearchError(null);
            }}
          />
          <Button variant="secondary" onClick={handleSearch} isLoading={isSearching} disabled={!searchId.trim()}>
            Find
          </Button>
        </div>
        {searchError && <p className="mt-1.5 text-xs text-danger">{searchError}</p>}

        {found && (
          <div className="mt-4 space-y-4 border-t border-surface-border pt-4">
            <div className="flex items-center gap-3">
              <Avatar src={found.avatar_url} className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-ink">{found.username}</p>
                <p className="font-mono text-xs text-ink-faint">ID: {found.user_id}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-ink-faint">Diamond balance</p>
                <p className="font-semibold text-gold">{found.diamond_balance.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-faint">Coin balance</p>
                <p className="font-semibold text-ink">{found.coin_balance.toLocaleString()}</p>
              </div>
            </div>

            <Select label="Action" value={direction} onChange={(e) => setDirection(e.target.value as "send" | "deduct")}>
              <option value="send">Send diamonds</option>
              <option value="deduct">Deduct diamonds</option>
            </Select>

            <Input
              label="Amount"
              type="number"
              min={1}
              placeholder="e.g. 500"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            <Textarea
              label="Reason (required — recorded in the ledger and audit log)"
              placeholder="e.g. Compensation for a reported bug, Refund for failed purchase..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />

            <Button
              variant={direction === "deduct" ? "danger" : "primary"}
              onClick={openConfirm}
              disabled={!amount || Number(amount) <= 0 || !reason.trim()}
            >
              {direction === "send" ? "Send Diamonds" : "Deduct Diamonds"}
            </Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => !isSubmitting && setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
        isLoading={isSubmitting || isRefreshingConfirm}
        isDangerous={direction === "deduct"}
        title={direction === "send" ? "Confirm sending diamonds" : "Confirm deducting diamonds"}
        confirmLabel={isRefreshingConfirm ? "Loading balance…" : direction === "send" ? "Send" : "Deduct"}
        description={
          found
            ? isRefreshingConfirm || confirmBalance === null
              ? "Loading the current balance…"
              : `${direction === "send" ? "Send" : "Deduct"} ${Number(amount).toLocaleString()} diamonds ${
                  direction === "send" ? "to" : "from"
                } ${found.username} (ID: ${found.user_id}). Current balance: ${confirmBalance.toLocaleString()}${
                  direction === "deduct" ? ` — new balance will be ${(confirmBalance - Number(amount)).toLocaleString()}` : ""
                }. Reason: "${reason.trim()}"`
            : ""
        }
      />
    </AppShell>
  );
}
